import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS } from "@/data/content";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled ? "bg-warm/95 border-b hairline backdrop-blur-sm" : "bg-transparent border-b border-transparent"
      }`}
    >
      <nav className="mx-auto max-w-[1500px] px-6 md:px-10 h-[76px] flex items-center justify-between">
        <Link to="/" data-testid="nav-logo-link" className="flex items-center" aria-label="HM Beacon home">
          <img src="/hm-beacon-logo.png" alt="HM Beacon" className="h-7 md:h-8 w-auto" />
        </Link>

        <div className="hidden lg:flex items-center gap-6 xl:gap-9">
          {NAV_LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.to.replace("/", "")}`}
              className={`text-sm font-body tracking-wide link-underline whitespace-nowrap ${
                pathname === l.to ? "text-navy" : "text-ink/70 hover:text-navy"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </div>

        <div className="flex items-center gap-4">
          <Link
            to="/contact"
            data-testid="nav-cta-button"
            className="hidden xl:inline-flex items-center bg-navy text-warm text-sm px-6 py-3 hover:bg-navy/90 transition-colors duration-300 whitespace-nowrap"
          >
            Map Your Visibility
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-navy p-2"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-warm border-t hairline"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {NAV_LINKS.map((l) => (
                <Link key={l.to} to={l.to} className="text-lg font-heading text-navy" data-testid={`mobile-nav-${l.to.replace("/", "")}`}>
                  {l.label}
                </Link>
              ))}
              <Link to="/contact" className="mt-2 bg-navy text-warm text-center py-3">Map Your Visibility</Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
